import { useApolloClient, useQuery } from '@apollo/client/react';
import { Box, LinearProgress, TableContainer } from '@mui/material';
import * as Sentry from '@sentry/react';
import type { JWT } from 'aws-amplify/auth';
import { format } from 'date-fns/fp';
import { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Flip, toast } from 'react-toastify';

import { NotificationToasts, OfflineAlert, Sidebar, TopBar } from './components';
import {
  DataLoadingContext,
  refreshLoadedKitchenComponents,
  refreshLoadedKitchenFoods,
  useHourlyDataSubscription,
  useKitchenLiveUpdates,
  useQueryErrorToast
} from './hooks';
import { Country, KDSAppContext, Kitchen, ListKitchensQuery, Session } from './libs';
import { LIST_KITCHENS_QUERY } from './libs/graphQL';
import { getActiveWorkingDayDate, getKitchenFromLocalStorage, parseCognitoUser, useDocumentMedia } from './libs/utils';
import LandOnToday from './views/Home/LandOnToday';

export const AppContext = createContext<KDSAppContext>({
  isOffline: false,
  isSidebarOpened: true,
  isFilterOpen: false,
  user: {
    id: '',
    name: '',
    email: '',
    phoneNumber: '',
    permissions: [],
    country: Country.BH,
    kitchen: [Kitchen.BH001],
    group: ''
  },
  kdsKitchen: null,
  kitchen: Kitchen.BH001,
  date: '',
  shift: Session.all,
  refetchFoods: () => {},
  setIsFilterOpen: () => false
});

export interface AppProps {
  signOut: () => void;
  user: JWT['payload'];
  children: ReactNode;
}

function App({ signOut, user, children }: AppProps) {
  const { isMobile, isTablet } = useDocumentMedia();
  const [isOpen, setIsOpen] = useState(true);
  const [kitchen, setKitchen] = useState<Kitchen>(getKitchenFromLocalStorage() as Kitchen);
  const [date, setDate] = useState(format('yyyy-MM-dd')(getActiveWorkingDayDate()));
  const [isOffline, setIsOffline] = useState(false);
  const [shift, setShift] = useState<Session>(Session.all);
  const [pendingDataLoads, setPendingDataLoads] = useState(0);

  const [openFilters, setOpenFilters] = useState(false);
  const mappedUser = useMemo(() => parseCognitoUser(user), [user]);

  // Set Sentry user context for error tracking
  useEffect(() => {
    if (mappedUser?.id) {
      Sentry.setUser({
        id: mappedUser.id,
        email: mappedUser.email,
        username: mappedUser.name
      });
    }

    // Clear user on unmount (logout)
    return () => {
      Sentry.setUser(null);
    };
  }, [mappedUser?.id, mappedUser?.email, mappedUser?.name]);

  useEffect(() => {
    if (isMobile || isTablet) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
    }
  }, [isMobile, isTablet]);

  // Screens read foods and components themselves (useKitchenFoods / useKitchenComponents), so App never watches them.
  const client = useApolloClient();
  const refreshFoods = useCallback(() => refreshLoadedKitchenFoods(client, kitchen, date), [client, kitchen, date]);
  const refreshComponents = useCallback(() => refreshLoadedKitchenComponents(client, kitchen, date), [client, kitchen, date]);
  const reportDataLoading = useCallback((delta: 1 | -1) => setPendingDataLoads((pending) => pending + delta), []);

  useKitchenLiveUpdates(kitchen);

  useHourlyDataSubscription({
    currentKitchen: kitchen,
    currentDate: date,
    onRefetchFoods: refreshFoods,
    onRefetchComponents: refreshComponents,
    enabled: true,
    showNotifications: true
  });

  const {
    loading: kitchenSessionLoading,
    data: kdsKitchens,
    error: kdsKitchensError
  } = useQuery<ListKitchensQuery>(LIST_KITCHENS_QUERY, {
    variables: { kitchen }
  });

  useQueryErrorToast(kdsKitchensError, 'Error while loading Kitchen Sessions');

  useEffect(() => {
    const kitchenConfig = kdsKitchens?.listKitchens?.data?.find((KDSKitchen) => KDSKitchen.id === kitchen);
    let kSession = kitchenConfig?.sessions;
    if (kitchenConfig?.sessionsPerDay) {
      const dayOfTheWeek = format('EEEE')(new Date(date));
      const sessionsPerDay = kitchenConfig?.sessionsPerDay;
      kSession = sessionsPerDay.find((session) => session.day === dayOfTheWeek)?.session;
    }

    if (kSession === 'all') {
      setShift(Session.all);
    } else {
      setShift(Session.morning);
    }
  }, [kdsKitchens, kitchen, date]);

  const kdsKitchen = useMemo(
    () => kdsKitchens?.listKitchens?.data?.find((KDSKitchen) => KDSKitchen.id === kitchen),
    [kdsKitchens, kitchen]
  );

  useEffect(() => {
    const handleOffline = () => {
      setIsOffline(true);
      toast.dismiss('toastify-online');
      toast(<OfflineAlert isOffline={true} />, {
        className: 'toastify-offline',
        hideProgressBar: true,
        closeOnClick: false,
        position: 'top-center',
        closeButton: false,
        autoClose: false,
        toastId: 'toastify-offline'
      });
    };
    const handleOnline = () => {
      setIsOffline(false);
      toast.update('toastify-offline', {
        render: <OfflineAlert isOffline={false} />,
        closeOnClick: true,
        autoClose: 3000,
        toastId: 'toastify-online',
        transition: Flip
      });
      void refreshFoods();
      void refreshComponents();
    };

    globalThis.addEventListener('offline', handleOffline);
    globalThis.addEventListener('online', handleOnline);
    return () => {
      globalThis.removeEventListener('offline', handleOffline);
      globalThis.removeEventListener('online', handleOnline);
    };
  }, [refreshFoods, refreshComponents]);

  useEffect(() => {
    setOpenFilters(false);
  }, [kitchen, date]);

  const closeFilters = useCallback(() => setOpenFilters(false), []);
  const appContextValue = useMemo(
    () => ({
      isOffline,
      isSidebarOpened: isOpen,
      isFilterOpen: openFilters,
      user: mappedUser,
      refetchFoods: () => void refreshFoods(),
      setIsFilterOpen: closeFilters,
      kdsKitchen: kdsKitchen ?? null,
      kitchen,
      date,
      shift
    }),
    [isOffline, isOpen, openFilters, mappedUser, refreshFoods, closeFilters, kdsKitchen, kitchen, date, shift]
  );

  return (
    <AppContext.Provider value={appContextValue}>
      <DataLoadingContext.Provider value={reportDataLoading}>
        <NotificationToasts kitchen={kitchen} date={date} />
        <Sidebar
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          signOut={signOut}
          date={date}
          setDate={setDate}
          kitchen={kitchen}
          setKitchen={setKitchen}
          user={mappedUser}
        />
        <TopBar
          setIsOpen={setIsOpen}
          isOpen={isOpen}
          kitchen={kitchen}
          date={date}
          openFilters={openFilters}
          setOpenFilters={setOpenFilters}
        />
        {(pendingDataLoads > 0 || kitchenSessionLoading) && (
          <Box sx={{ width: '100%', zIndex: 10 }}>
            <LinearProgress />
          </Box>
        )}
        <Box
          sx={{
            overflow: 'auto',
            height: '100%',
            width: { sm: isOpen ? `calc(100% - ${260}px)` : '100%' },
            ml: { sm: isOpen ? '260px' : '0' }
          }}
        >
          <TableContainer sx={{ display: 'inline-flex', height: '100%', width: '100%' }}>
            <LandOnToday />
            {children}
          </TableContainer>
        </Box>
      </DataLoadingContext.Provider>
    </AppContext.Provider>
  );
}
export default App;
